import { Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import cStyles from '../../../assets/scss/pages/CreateOrder.module.scss';
import styles from '../../../assets/scss/pages/PackageSell.module.scss';
import KeypadInput from '../../../components/common/KeypadInput';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import useCartStore, {
    resolvePackagePrice,
    resolvePackageSellerCommission,
} from '../../../stores/cartStore';

// Port từ app: screens/employee/PackageItemEditModal.js.
// Override giá / số buổi / HSD / ghi chú khi bán gói. Cần quyền PACKAGE.SELL_OVERRIDE.

const formatVnd = (n) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

export default function PackageItemEditModal({ open, onClose, customerId, uid }) {
    const { hasPermission } = usePermissions();
    const canOverride = hasPermission(PERMISSIONS.PACKAGE.SELL_OVERRIDE);

    const item = useCartStore((s) =>
        (s.packageCartByCustomer[customerId] || []).find((it) => it._uid === uid),
    );
    const updatePackageItem = useCartStore((s) => s.updatePackageItem);
    const removePackageItem = useCartStore((s) => s.removePackageItem);

    const [customPrice, setCustomPrice] = useState('');
    const [customSessions, setCustomSessions] = useState('');
    const [customValidityMonths, setCustomValidityMonths] = useState('');
    const [overrideReason, setOverrideReason] = useState('');
    const [note, setNote] = useState('');
    const [internalNote, setInternalNote] = useState('');
    const [reasonError, setReasonError] = useState('');

    useEffect(() => {
        if (open && item) {
            setCustomPrice(item.customPrice != null ? String(item.customPrice) : '');
            setCustomSessions(item.customSessions != null ? String(item.customSessions) : '');
            setCustomValidityMonths(item.customValidityMonths != null ? String(item.customValidityMonths) : '');
            setOverrideReason(item.overrideReason ?? '');
            setNote(item.note ?? '');
            setInternalNote(item.internalNote ?? '');
            setReasonError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, uid]);

    if (!open || !canOverride || !item) return null;

    // Real-time preview
    const previewItem = {
        ...item,
        customPrice: customPrice === '' ? null : Number(customPrice),
        customSessions: customSessions === '' ? null : Number(customSessions),
    };
    const previewPrice = resolvePackagePrice(previewItem);
    const previewCommission = resolvePackageSellerCommission(previewItem);

    const priceOverridden = customPrice !== '' && Number(customPrice) !== Number(item.templatePrice);
    const sessionsOverridden = customSessions !== '' && Number(customSessions) !== Number(item.templateSessions);
    const validityOverridden =
        customValidityMonths !== '' && Number(customValidityMonths) !== Number(item.templateValidityMonths);
    const isOverridden = priceOverridden || sessionsOverridden || validityOverridden;

    const handleSave = () => {
        if (isOverridden && overrideReason.trim().length < 5) {
            setReasonError('Lý do điều chỉnh phải từ 5 ký tự');
            return;
        }
        updatePackageItem(customerId, uid, {
            customPrice: customPrice === '' ? null : Number(customPrice),
            customSessions: customSessions === '' ? null : Number(customSessions),
            customValidityMonths: customValidityMonths === '' ? null : Number(customValidityMonths),
            overrideReason: overrideReason.trim() || null,
            note: note.trim() || null,
            internalNote: internalNote.trim() || null,
        });
        onClose();
    };

    const handleRemove = () => {
        if (window.confirm(`Xoá gói khỏi giỏ?\n${item.name} ×${item.quantity}`)) {
            removePackageItem(customerId, uid);
            onClose();
        }
    };

    return (
        <div className={cStyles.sideModalOverlay} onClick={onClose}>
            <div className={cStyles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={cStyles.modalHeader}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 className={cStyles.modalTitle}>{item.name}</h3>
                        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                            Số lượng: {item.quantity} thẻ · Hoa hồng NV bán{' '}
                            {Number(item.sellerCommissionPercent || 0)}% = {formatVnd(previewCommission)}/thẻ
                        </div>
                    </div>
                    <button className={cStyles.modalClose} onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <div className={styles.body} style={{ flex: 1, minHeight: 0 }}>
                    <div className={styles.row2col}>
                        <div className={styles.field}>
                            <div className={styles.labelRow}>
                                <span className={styles.label}>Số buổi</span>
                                <span className={styles.originChip}>Gốc {item.templateSessions} buổi</span>
                            </div>
                            <KeypadInput
                                className={`${styles.input}${sessionsOverridden ? ` ${styles.inputOverridden}` : ''}`}
                                overlayClassName={cStyles.keypadRightOverlay}
                                value={customSessions}
                                onChange={(raw) => setCustomSessions(raw)}
                                placeholder={String(item.templateSessions)}
                                title="Số buổi"
                                mode="integer"
                                allowDecimal={false}
                            />
                        </div>
                        <div className={styles.field}>
                            <div className={styles.labelRow}>
                                <span className={styles.label}>HSD</span>
                                <span className={styles.originChip}>
                                    {item.templateValidityMonths
                                        ? `Gốc ${item.templateValidityMonths} tháng`
                                        : 'Vô hạn'}
                                </span>
                            </div>
                            <KeypadInput
                                className={`${styles.input}${validityOverridden ? ` ${styles.inputOverridden}` : ''}`}
                                overlayClassName={cStyles.keypadRightOverlay}
                                value={customValidityMonths}
                                onChange={(raw) => setCustomValidityMonths(raw)}
                                placeholder={item.templateValidityMonths ? String(item.templateValidityMonths) : 'Vô hạn'}
                                title="HSD (tháng)"
                                mode="integer"
                                allowDecimal={false}
                            />
                        </div>
                    </div>

                    <div className={styles.field} style={{ marginBottom: 6 }}>
                        <div className={styles.labelRow}>
                            <span className={styles.label}>Giá thực bán mỗi thẻ</span>
                            <span className={styles.originChip}>
                                Gốc {formatVnd(item.templatePrice)}
                                {item.templateSalePrice != null ? ` · KM ${formatVnd(item.templateSalePrice)}` : ''}
                            </span>
                        </div>
                        <KeypadInput
                            className={`${styles.input}${priceOverridden ? ` ${styles.inputOverridden}` : ''}`}
                            overlayClassName={cStyles.keypadRightOverlay}
                            value={customPrice}
                            onChange={(raw) => setCustomPrice(raw)}
                            placeholder={String(
                                item.templateSalePrice != null ? item.templateSalePrice : item.templatePrice,
                            )}
                            title="Giá thực bán mỗi thẻ"
                            mode="money"
                        />
                        <div className={styles.previewHint}>
                            Đang áp:{' '}
                            {priceOverridden && <span className={styles.oldPrice}>{formatVnd(item.templatePrice)}</span>}
                            {formatVnd(previewPrice)}/thẻ × {item.quantity} = {formatVnd(previewPrice * item.quantity)}
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <span className={styles.label}>
                                Lý do điều chỉnh {isOverridden && <span className={styles.required}>*</span>}
                            </span>
                        </div>
                        <textarea
                            className={`${styles.input} ${styles.textarea}`}
                            value={overrideReason}
                            onChange={(e) => {
                                setOverrideReason(e.target.value);
                                if (reasonError) setReasonError('');
                            }}
                            placeholder={isOverridden ? 'Bắt buộc nếu sửa giá/buổi/HSD' : 'Không bắt buộc'}
                        />
                        {reasonError ? (
                            <div className={styles.previewHint} style={{ color: '#ef4444' }}>
                                {reasonError}
                            </div>
                        ) : null}
                    </div>

                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <span className={styles.label}>Ghi chú khách (in lên phiếu)</span>
                        </div>
                        <textarea
                            className={`${styles.input} ${styles.textarea}`}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Vd: Ưu tiên thứ 7 chiều"
                        />
                    </div>

                    <div className={styles.field} style={{ marginBottom: 0 }}>
                        <div className={styles.labelRow}>
                            <span className={styles.label}>Ghi chú nội bộ (chỉ NV thấy)</span>
                        </div>
                        <textarea
                            className={`${styles.input} ${styles.textarea}`}
                            value={internalNote}
                            onChange={(e) => setInternalNote(e.target.value)}
                            placeholder="Vd: Khách dị ứng AHA"
                        />
                    </div>
                </div>

                <div className={styles.footer}>
                    <button className={styles.dangerBtn} onClick={handleRemove}>
                        <Trash2 size={18} /> Xoá
                    </button>
                    <button className={styles.primaryBtn} onClick={handleSave}>
                        Lưu
                    </button>
                </div>
            </div>
        </div>
    );
}
